"use client";

import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, CircleCheck } from "lucide-react";
import { GradientTextParser } from "@/components/shared/content/TextCombination";
import { Tag } from "@/components/ui/tag";

import type { AlternativeOfferProps } from "./AlternativeOffer";

type OfferHeroProps = Pick<AlternativeOfferProps,
  | "label" | "heading" | "subtitle"
  | "trialPrompt" | "trialLabel" | "trialLink"
  | "comparisonPrompt" | "comparisonLabel" | "comparisonLink"
> & {
  children: ReactNode;
  tabListLabel?: string;
  tabs: (AlternativeOfferProps["tabs"][number] & { content?: ReactNode })[];
};

export default function DatadogOfferHero({
  label, heading, subtitle, tabs, trialPrompt, trialLabel, trialLink,
  comparisonPrompt, comparisonLabel, comparisonLink, children,
  tabListLabel = "About your savings estimate",
}: OfferHeroProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight": nextIndex = (index + 1) % tabs.length; break;
      case "ArrowLeft": nextIndex = (index - 1 + tabs.length) % tabs.length; break;
      case "Home": nextIndex = 0; break;
      case "End": nextIndex = tabs.length - 1; break;
      default: return;
    }
    event.preventDefault();
    setActiveIndex(nextIndex);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    <section aria-labelledby="datadog-offer-heading" className="container mx-auto px-0 py-12 md:py-20 xl:px-[54px]">
      <div className="grid items-start gap-10 lg:items-stretch lg:grid-cols-[1.1fr_1fr] lg:gap-12 xl:gap-20">
        <div className="flex min-w-0 flex-col lg:pt-4">
          {label && <Tag variant="content">{label}</Tag>}
          <h1 id="datadog-offer-heading" className={`${label ? "mt-6 " : ""}max-w-xl text-[36px] font-normal leading-[1.15] tracking-tight text-white md:text-[48px] xl:text-[56px]`}>
            <GradientTextParser text={heading} />
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-7 text-grey-550">
            {subtitle}
          </p>

          <div role="tablist" aria-label={tabListLabel} className="mt-9 grid grid-cols-2 gap-1 border-b border-white/15 sm:flex sm:flex-wrap sm:gap-0">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                ref={(element) => { tabRefs.current[index] = element; }}
                type="button"
                role="tab"
                id={`offer-tab-${tab.id}`}
                aria-controls={`offer-panel-${tab.id}`}
                aria-selected={activeIndex === index}
                tabIndex={activeIndex === index ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`-mb-px cursor-pointer border-b-2 px-3 py-3 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white ${activeIndex === index ? "border-[#F8494D] text-white" : "border-transparent text-grey-550 hover:text-white"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {tabs.map((tab, index) => (
            <div key={tab.id} role="tabpanel" id={`offer-panel-${tab.id}`} aria-labelledby={`offer-tab-${tab.id}`} hidden={activeIndex !== index} tabIndex={0} className="min-h-[270px] py-7 focus-visible:outline-2 focus-visible:outline-white">
              {tab.content ?? (tab.numbered && tab.points ? (
                <ol className="space-y-6">
                  {tab.points.map((point, pointIndex) => (
                    <li key={typeof point === "string" ? point : point.title} className="relative flex gap-4 text-base leading-6 text-grey-550">
                      {pointIndex < (tab.points?.length ?? 0) - 1 && <span aria-hidden="true" className="absolute left-3 top-6 h-full w-px bg-gradient-to-b from-[#F8494D] to-[#FFB469]/30" />}
                      <span aria-hidden="true" className="relative flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F8494D] to-[#FFB469] text-sm font-medium text-black ring-4 ring-[#141414]">{pointIndex + 1}</span>
                      {typeof point === "string" ? <span>{point}</span> : (
                        <div>
                          <p className="font-medium text-white">{point.title}</p>
                          <p className="mt-1">{point.description}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              ) : tab.points ? (
                <>
                  {tab.heading && <h2 className="text-lg font-medium text-white">{tab.heading}</h2>}
                  <ul className={tab.heading ? "mt-5 space-y-4" : "space-y-4"}>
                    {tab.points.map((point) => (
                      <li key={typeof point === "string" ? point : point.title} className="flex gap-3 text-base leading-6 text-grey-550">
                        <CircleCheck aria-hidden="true" className="mt-1 size-4 shrink-0 text-[#F8494D]" />
                        {typeof point === "string" ? <span>{point}</span> : (
                          <div>
                            {point.label && <p className="mb-1 text-sm">{point.label}</p>}
                            <p className="font-medium text-white">{point.title}</p>
                            <p className="mt-1">{point.description}</p>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <dl className="space-y-5 text-lg leading-xxl tracking-wide">
                  {tab.answers?.map(({ question, answer }) => (
                    <div key={question}>
                      <dt className="font-medium text-white">{question}</dt>
                      <dd className="mt-1 text-grey-550">{answer}</dd>
                    </div>
                  ))}
                </dl>
              ))}
              {tab.note && <p className="mt-5 text-base leading-6 text-grey-550">{tab.note}</p>}
            </div>
          ))}

          <div className="mt-auto space-y-4 border-t border-white/15 pt-6 text-sm text-grey-550">
            <p>{trialPrompt}{" "}<Link href={trialLink} className="inline-flex items-center gap-1 text-white underline-offset-4 hover:underline">{trialLabel} <ArrowRight aria-hidden="true" className="size-4" /></Link></p>
            <p>{comparisonPrompt}{" "}<Link href={comparisonLink} className="inline-flex items-center gap-1 text-white underline-offset-4 hover:underline">{comparisonLabel} <ArrowRight aria-hidden="true" className="size-4" /></Link></p>
          </div>
        </div>
        <div className="w-full min-w-0">{children}</div>
      </div>
    </section>
  );
}
